'use client';

import { FC } from 'react';
import { useAtomValue } from 'jotai';
import { ethers } from 'ethers';
import {
  proposalThresholdAtom,
  votingPeriodAtom,
  votingDelayAtom,
  circulatingSupplyAtom,
  fundingQuorumAtom,
  constitutionalQuorumAtom,
} from '@/app/atoms/governorContract';
import { totalDelegationPowerLoadable } from '@/app/atoms/delegates';
import ClipboardCopy from '../ClipboardCopy';
import BlockchainExplorerLink from '../BlockchainExplorerLink';
import { abbreviateValue } from '@/app/helpers/abbreviateValue';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Tooltip from '../Tooltip';

type TooltipProps = {
  className?: string;
  tooltipAlign?: 'start' | 'center' | 'end';
  tooltipTitle?: string;
  tooltipValue?: string;
};

const Parameters: FC<TooltipProps> = () => {
  const proposalThreshold = useAtomValue(proposalThresholdAtom);
  const formattedVotingPeriod = useAtomValue(votingPeriodAtom);
  const formattedVotingDelay = useAtomValue(votingDelayAtom);
  const circulatingSupply = useAtomValue(circulatingSupplyAtom);
  const fundingQuorumNeeded = useAtomValue(fundingQuorumAtom);
  const constitutionalQuorumNeeded = useAtomValue(constitutionalQuorumAtom);

  const powerLoadable = useAtomValue(totalDelegationPowerLoadable);

  const currentDelegationPower =
    powerLoadable.state === 'hasData' ? powerLoadable.data : 0;

  const governor = process.env.NEXT_PUBLIC_GOVERNOR_ADDRESS!;
  const tokenAddress = process.env.NEXT_PUBLIC_TOKEN_ADDRESS!;
  const timelockAddress = process.env.NEXT_PUBLIC_TIMELOCK_ADDRESS!;

  const fundingQuorum = abbreviateValue(Number(fundingQuorumNeeded));
  const constitutionalQuorum = abbreviateValue(Number(constitutionalQuorumNeeded));
  const abbreviatedDelegationPower = abbreviateValue(currentDelegationPower);

  const formattedCirculatingSupply = Number(circulatingSupply?.replace(/,/g, ''));
  const abbreviatedCirculatingSupply = abbreviateValue(formattedCirculatingSupply);

  return (
    <div className="flex flex-col justify-between sm:flex-row px-4 mt-8 max-sm:text-center sm:rounded-md py-4 border border-neutral-700 gap-4">
      <div className="flex-grow">
        <div className="font-bold text-yellow">Contract Parameters</div>
        <div className="flex flex-col mt-5 gap-4 pr-4">
          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Proposal Threshold
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Threshold</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The minimum number of tokens that must be delegated to a holder for
                    them to create a proposal in the DAO.
                  </div>
                </Tooltip>
              </div>
              <div>{proposalThreshold}</div>
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Votable Supply
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Votable</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The total number of tokens currently in circulation and available to
                    the public. The quorum needed for proposals is based on this supply.
                  </div>
                </Tooltip>
              </div>
              <div>{abbreviatedCirculatingSupply}</div>
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Funding Quorum
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Funding</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The minimum number of votes required for a funding request to pass and
                    proceed to execution, based on the voteable supply.
                  </div>
                </Tooltip>
              </div>
              <div>{fundingQuorum}</div>
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Constitutional Quorum
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Constitutional</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The minimum number of votes required for a constitutional change to
                    pass and proceed to execution, based on the voteable supply.
                  </div>
                </Tooltip>
              </div>
              <div>{constitutionalQuorum}</div>
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Delegated Power
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Delegated</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    Total delegated power is the combined voting power of all currently
                    delegated tokens.
                  </div>
                </Tooltip>
              </div>
              <div>{abbreviatedDelegationPower ?? '...'}</div>
            </div>
          </div>

          <div className="flex">
            <div className="flex justify-between min-w-full">
              <div className="flex pr-4">
                <div className="whitespace-nowrap hidden lg:flex mr-1">
                  Proposal delay
                </div>
                <div className="whitespace-nowrap lg:hidden mr-1">Delay</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The time period between the proposal creation and when voting begins,
                    allowing members to review the proposal.
                  </div>
                </Tooltip>
              </div>
              <div>{formattedVotingDelay ?? '0 days'}</div>
            </div>
          </div>

          <div className="flex w-full">
            <div className="flex justify-between w-full">
              <div className="flex mr-1">
                <div className="whitespace-nowrap hidden lg:flex mr-1">Voting open</div>
                <div className="whitespace-nowrap lg:hidden mr-1">Open</div>
                <Tooltip
                  className="min-w-[175px]"
                  hover
                  arrow
                  trigger={<InformationCircleIcon className="w-5 h-5 text-yellow" />}
                >
                  <div>
                    The period during which members can cast their votes on a proposal.
                  </div>
                </Tooltip>
              </div>
              <div>{formattedVotingPeriod ?? '0 weeks'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow">
        <div className="font-bold text-yellow">Contract Addresses</div>
        <div className="flex flex-col mt-5 gap-4">
          {/* Governor */}
          <div className="flex justify-between">
            <div>Governor</div>
            <div className="flex whitespace-nowrap z-0">
              {governor ? (
                <>
                  <span className="block 260:hidden">
                    <ClipboardCopy address={governor} truncate="xs" />
                  </span>
                  <span className="hidden 260:block 300:hidden">
                    <ClipboardCopy address={governor} truncate="64" />
                  </span>
                  <span className="hidden sm:block ipad:hidden xs:hidden 400:hidden 300:block">
                    <ClipboardCopy address={governor} truncate="md" />
                  </span>
                  <span className="hidden ipad:block params:hidden wide:hidden 400:block">
                    <ClipboardCopy address={governor} truncate="xl" />
                  </span>
                  <span className="hidden wide:block narrow:hidden params:block ipad:hidden sm:hidden">
                    <ClipboardCopy address={governor} />
                  </span>
                  <BlockchainExplorerLink address={governor} />
                </>
              ) : (
                <>
                  <span className="hidden md:block">
                    <ClipboardCopy address={ethers.ZeroAddress} />
                  </span>
                  <BlockchainExplorerLink address={ethers.ZeroAddress} />
                </>
              )}
            </div>
          </div>

          {/* Token */}
          <div className="flex justify-between">
            <div>Token</div>
            <div className="flex whitespace-nowrap">
              {tokenAddress ? (
                <>
                  <span className="block 260:hidden">
                    <ClipboardCopy address={tokenAddress} truncate="xs" />
                  </span>
                  <span className="hidden 260:block 300:hidden">
                    <ClipboardCopy address={tokenAddress} truncate="64" />
                  </span>
                  <span className="hidden sm:block ipad:hidden xs:hidden 400:hidden 300:block">
                    <ClipboardCopy address={tokenAddress} truncate="md" />
                  </span>
                  <span className="hidden ipad:block params:hidden wide:hidden 400:block">
                    <ClipboardCopy address={tokenAddress} truncate="xl" />
                  </span>
                  <span className="hidden wide:block narrow:hidden params:block ipad:hidden sm:hidden">
                    <ClipboardCopy address={tokenAddress} />
                  </span>
                  <BlockchainExplorerLink address={tokenAddress} />
                </>
              ) : (
                <>
                  <span className="hidden md:block">
                    <ClipboardCopy address={ethers.ZeroAddress} />
                  </span>
                  <BlockchainExplorerLink address={ethers.ZeroAddress} />
                </>
              )}
            </div>
          </div>

          {/* Timelock */}
          <div className="flex justify-between">
            <div>Timelock</div>
            <div className="flex whitespace-nowrap">
              {timelockAddress ? (
                <>
                  <span className="block 260:hidden">
                    <ClipboardCopy address={timelockAddress} truncate="xs" />
                  </span>
                  <span className="hidden 260:block 300:hidden">
                    <ClipboardCopy address={timelockAddress} truncate="64" />
                  </span>
                  <span className="hidden sm:block ipad:hidden xs:hidden 400:hidden 300:block">
                    <ClipboardCopy address={timelockAddress} truncate="md" />
                  </span>
                  <span className="hidden ipad:block params:hidden wide:hidden 400:block">
                    <ClipboardCopy address={timelockAddress} truncate="xl" />
                  </span>
                  <span className="hidden wide:block narrow:hidden params:block ipad:hidden sm:hidden">
                    <ClipboardCopy address={timelockAddress} />
                  </span>
                  <BlockchainExplorerLink address={timelockAddress} />
                </>
              ) : (
                <>
                  <span className="hidden md:block text-xs">
                    <ClipboardCopy address={ethers.ZeroAddress} />
                  </span>
                  <BlockchainExplorerLink address={ethers.ZeroAddress} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Parameters;
